import { type FC, type PropsWithChildren, useContext } from 'react';

import type { QueryClient } from '@tanstack/react-query';

import type { AppConfig } from '@cognite/apm-config';
import type { AuthStateUser } from '@cognite/e2e-auth';
import type { CogniteClient } from '@cognite/sdk';
import { useTranslation } from 'react-i18next';

import { ReactQueryDevtoolsWrapper } from '../utils/react-query-devtools-wrapper';

import { AppProvidersSwitcherContext } from './app-providers-switcher.context';

/**
 * AppProviderSwitcher - Determines if project is DM or Classic and renders appropriate providers
 *
 * This component is the split point between DM and Classic apps. It uses useShouldUseCdmMode()
 * based on local data modeling override, feature flag and CDM access group membership, then renders:
 * - PageLoader while determining project type
 * - CdmProviders for DM projects when feature flag is enabled
 * - ClassicProviders for Classic/Hybrid projects or when feature flag is disabled
 */
export const AppProviderSwitcher: FC<
  PropsWithChildren<{
    i18nLanguage?: string;
    authClient: CogniteClient;
    queryClient: QueryClient;
    fdmServiceProviderMock?: {
      appConfig: AppConfig;
      client?: CogniteClient;
      user?: AuthStateUser;
    };
    userMock?: AuthStateUser;
  }>
> = ({
  children,
  i18nLanguage,
  authClient,
  queryClient,
  fdmServiceProviderMock,
  userMock,
}) => {
  const {
    PageLoader,
    CdmProviders,
    ClassicProviders,
    useShouldUseCdmMode,
    AppConfigErrorScreen,
  } = useContext(AppProvidersSwitcherContext);

  const { shouldUseCdmMode, isLoading, isError } = useShouldUseCdmMode();
  const { t } = useTranslation('user');

  if (isError) {
    return (
      <AppConfigErrorScreen
        title={t(
          'USER_ERROR_LOCATION_MISSING_TITLE',
          'Error: Location is missing.'
        )}
        body={t(
          'USER_ERROR_LOCATION_MISSING_DESCRIPTION',
          "The InField admin has not defined any location yet, or you don't have access to any of the configured locations. Contact your admin if you have any questions."
        )}
      />
    );
  }
  if (isLoading) {
    return (
      <PageLoader
        infoText={t('LOADING_APPLICATION', 'loading application...')}
      />
    );
  }

  if (shouldUseCdmMode) {
    return (
      <CdmProviders
        i18nLanguage={i18nLanguage}
        authClient={authClient}
        queryClient={queryClient}
        fdmServiceProviderMock={fdmServiceProviderMock}
        userMock={userMock}
      >
        <ReactQueryDevtoolsWrapper />
        {children}
      </CdmProviders>
    );
  }
  return (
    <ClassicProviders
      i18nLanguage={i18nLanguage}
      authClient={authClient}
      queryClient={queryClient}
      fdmServiceProviderMock={fdmServiceProviderMock}
      userMock={userMock}
    >
      <ReactQueryDevtoolsWrapper />
      {children}
    </ClassicProviders>
  );
};
